import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Helmet } from 'react-helmet-async';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Breadcrumbs,
  Link,
  Card,
  CardHeader,
  CardContent,
  Table,
  Stack,
  Paper,
  TextField,
  Button,
  TableRow,
  TableBody,
  TableCell,
  Container,
  Typography,
  IconButton,
  TableContainer,
  TableHead,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Switch,
  Grid,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { SortableContainer, SortableHandle, SortableElement, arrayMove } from 'react-sortable-hoc';
import { arrayMoveImmutable } from 'array-move';
import Iconify from '../components/iconify';
import Scrollbar from '../components/scrollbar';
import { ListHead, ListToolbar } from '../components/list';
import categoryService from '../services/category.service';
import { getInitials } from '../utils/get-initials';
import { loading } from '../reducers/general.reducer';

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

const DragHandle = SortableHandle(({ style }) => (
  <span style={{ ...style, ...{ cursor: 'move' } }}>
    <Iconify icon="mdi:drag-horizontal-variant" />
  </span>
));

const TableBodySortable = SortableContainer(({ children }) => <TableBody>{children}</TableBody>);

TableBodySortable.muiName = 'TableBody';

const Row = SortableElement(({ data, handleOpenDelete, handleOpenAddEdit, onChangeActive, ...other }) => (
  <TableRow {...other}>
    <TableCell style={{ width: '5%' }}>
      <DragHandle />
    </TableCell>
    <TableCell>
      <Typography variant="subtitle2" noWrap>
        {data.name}
      </Typography>
    </TableCell>
    <TableCell>
      <Typography variant="body1" noWrap>
        {data.description}
      </Typography>
    </TableCell>
    <TableCell align="right">
      <Switch checked={data.active} color="success" onChange={() => onChangeActive && onChangeActive(data.id)} />
      <IconButton size="large" color="inherit" onClick={() => handleOpenAddEdit && handleOpenAddEdit(data)}>
        <Iconify icon={'eva:edit-outline'} />
      </IconButton>
      <IconButton size="large" color="error" onClick={() => handleOpenDelete && handleOpenDelete(data)}>
        <Iconify icon={'eva:trash-2-outline'} />
      </IconButton>
    </TableCell>
  </TableRow>
));

export default function Page() {
  const user = useSelector((state) => state.user);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [openDelete, setOpenDelete] = useState(false);
  const [openAddEdit, setOpenAddEdit] = useState(false);
  const [data, setData] = useState([]);
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState('asc');
  const [selected, setSelected] = useState([]);
  const [orderBy, setOrderBy] = useState('name');

  const [filterName, setFilterName] = useState('');
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [selectedRow, setSelectedRow] = useState(undefined);

  const fetchData = useCallback(async () => {
    dispatch(loading(true));
    const data = await categoryService.getAll(user.account, 1);
    setData(data);
    dispatch(loading(false));
  }, [dispatch, user.account]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: '',
      description: '',
      submit: null,
    },
    validationSchema: Yup.object({
      name: Yup.string().max(255).required('O NOME é obrigatório'),
    }),
    onSubmit: async (values, helpers) => {
      try {
        dispatch(loading(true));
        if (selectedRow && selectedRow.id) {
          await categoryService.edit(user.account, 1, selectedRow.id, {
            ...selectedRow,
            name: values.name,
            description: values.description,
          });
        } else {
          await categoryService.add(user.account, 1, {
            ...selectedRow,
            name: values.name,
            description: values.description,
          });
        }
        await fetchData();
        setOpenAddEdit(false);
        setSelectedRow(undefined);
      } catch (error) {
        let message = 'Ops! Algo de errado aconteceu';
        if (error.response && error.response.data && error.response.data.message[0]) {
          message = error.response.data.message.join(', ');
        }
        helpers.setStatus({ success: false });
        helpers.setErrors({ submit: message });
        helpers.setSubmitting(false);
      } finally {
        dispatch(loading(false));
      }
    },
  });

  const handleOpenDelete = (row) => {
    setSelectedRow(row);
    setOpenDelete(true);
  };

  const handleCloseDelete = () => {
    setSelectedRow(undefined);
    setOpenDelete(false);
  };

  const handleDelete = async () => {
    if (selectedRow) {
      dispatch(loading(true));
      await categoryService.remove(user.account, 1, selectedRow.id);
      const _data = [...data];
      const index = _data.map((e) => e.id).indexOf(selectedRow.id);
      _data.splice(index, 1);
      setData(_data);
      dispatch(loading(false));
    }
    handleCloseDelete();
  };

  const handleOpenAddEdit = (row) => {
    formik.setFieldValue('name', row ? row.name : '');
    formik.setFieldValue('description', row ? row.description : '');
    setSelectedRow(row);
    setOpenAddEdit(true);
  };

  const handleCloseAddEdit = () => {
    setSelectedRow(undefined);
    setOpenAddEdit(false);
  };

  const onChangeActive = async (id) => {
    const _data = [...data];
    const index = _data.map((e) => e.id).indexOf(id);
    _data[index] = {
      ..._data[index],
      active: !_data[index].active,
    };
    setData(_data);
    await categoryService.edit(user.account, 1, _data[index].id, { ..._data[index] });
  };

  const onSortEnd = async ({ oldIndex, newIndex }) => {
    const _data = arrayMoveImmutable(data, oldIndex, newIndex).map((item, index) => ({
      ...item,
      order: index + 1,
    }));
    setData(_data);
    _data.map(async (item) => categoryService.edit(user.account, 1, item.id, item));
  };

  return (
    <>
      <Helmet>
        <title> Categorias | BMENU </title>
      </Helmet>

      <Container>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="h4" gutterBottom>
            CATEGORIAS
          </Typography>
        </Stack>
        <Breadcrumbs aria-label="breadcrumb" mb={5}>
          <Link underline="hover" color="inherit" href="/dashboard/home">
            Dashboard
          </Link>
          <Typography color="text.primary">Menu</Typography>
          <Typography color="text.primary">Categorias</Typography>
        </Breadcrumbs>
        <Card>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell />
                  <TableCell>CATEGORIA</TableCell>
                  <TableCell>DESCRIÇÃO</TableCell>
                  <TableCell>
                    <Stack direction="row" justifyContent="end">
                      <Button variant="text" size="small" onClick={() => handleOpenAddEdit(undefined)}>
                        <Iconify icon="mdi:add" /> ADICIONAR
                      </Button>
                    </Stack>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBodySortable onSortEnd={onSortEnd} useDragHandle>
                {data.map((row, index) => (
                  <Row
                    index={index}
                    key={index}
                    data={row}
                    handleOpenDelete={() => handleOpenDelete(row)}
                    handleOpenAddEdit={() => handleOpenAddEdit(row)}
                    onChangeActive={() => onChangeActive(row.id)}
                  />
                ))}
              </TableBodySortable>
            </Table>
          </TableContainer>
        </Card>
      </Container>
      <Dialog
        open={openDelete}
        onClose={handleCloseDelete}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        fullWidth
      >
        <DialogTitle id="alert-dialog-title">{'Tem certeza de que quer excluir esta categoria?'}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">{selectedRow ? selectedRow.name : ''}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDelete}>CANCELAR</Button>
          <Button onClick={handleDelete} autoFocus sx={{ color: 'error.main' }}>
            CONFIRMAR
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={openAddEdit}
        onClose={handleCloseAddEdit}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        fullWidth
      >
        <form noValidate onSubmit={formik.handleSubmit}>
          <DialogTitle id="alert-dialog-title">{selectedRow ? 'EDITAR' : 'ADICIONAR'}</DialogTitle>
          <DialogContent>
            <Box sx={{ paddingTop: 1 }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    error={!!(formik.touched.name && formik.errors.name)}
                    fullWidth
                    helperText={formik.touched.name && formik.errors.name}
                    label="NOME"
                    name="name"
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    value={formik.values.name}
                    autoComplete="off"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    error={!!(formik.touched.description && formik.errors.description)}
                    fullWidth
                    helperText={formik.touched.description && formik.errors.description}
                    label="DESCRIÇÃO"
                    name="description"
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    value={formik.values.description}
                    autoComplete="off"
                  />
                </Grid>

                {formik.errors.submit && (
                  <Grid item xs={12} textAlign={'center'}>
                    <Typography color="error" variant="body2">
                      {formik.errors.submit} {formik.values.active}
                    </Typography>
                  </Grid>
                )}
                <Grid item xs={12} textAlign={'right'}>
                  <Switch
                    checked={selectedRow ? selectedRow.active : false}
                    color="success"
                    onChange={() => {
                      const _selectedRow = selectedRow
                        ? { ...selectedRow, active: !selectedRow.active }
                        : { active: false };
                      setSelectedRow(_selectedRow);
                    }}
                    name="active"
                  />
                </Grid>
              </Grid>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseAddEdit}>CANCELAR</Button>
            <Button type="submit" autoFocus sx={{ color: 'error.main' }}>
              CONFIRMAR
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </>
  );
}
